import moment from "moment";
import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useLazyViewSolarPlantAlertsAdminQuery } from "../../../../../redux/api/generation/generationAPI";
import SitesErrorLogTable from "../../../../../shared/components/site-error-log-table/site-error-log-table";
import IViewAdminSolarPlantAlertsRequestDTO from "../../../../../shared/oversight-core/dtos/request-dtos/view-admin-solar-plant-alerts-request-dto";
import IViewAdminSolarPlantAlertsResponseDTO from "../../../../../shared/oversight-core/dtos/response-dtos/view-admin-solar-plant-alerts-response-dto";
import { ESolarServiceProvider } from "../../../../../shared/oversight-core/enums/solar-service-provider";
import { IGoodWeSolarPlantAlert } from "../../../../../shared/oversight-core/interfaces/good-we-solar-plant-alert";
import { IVrmSolarPlantAlert } from "../../../../../shared/oversight-core/interfaces/vrm-solar-plant-alert";
import AppButton from "../../../../../shared/oversight-core/ui-elements/buttons/app-button/app-button";
import ButtonWithBadge from "../../../../../shared/oversight-core/ui-elements/buttons/button-with-badge/button-with-badge";
import DataTable from "../../../../../shared/oversight-core/ui-elements/data-table/data-table";
import MaterialIcon from "../../../../../shared/oversight-core/ui-elements/material-icon/material-icon";
import SpinnerModal from "../../../../../shared/oversight-core/ui-elements/spinner/spinner";
import {
  formatEpochToDate,
  getTimestampDuration,
} from "../../../../../shared/oversight-core/utils/time-utils";
import translate from "../../../../../shared/oversight-core/utils/translate";
import SiteErrorLogFilterPanel from "./site-error-log-filter-panel/site-error-log-filter-panel";

interface IProps {
  plantId: string;
  zoneId?: string;
  serviceProvider: ESolarServiceProvider;
}
interface IRequestParams {
  startDate?: string;
  endDate?: string;
}

const initialFilters: IRequestParams = {
  startDate: "",
  endDate: "",
};

const SiteErrorLog = (props: IProps) => {
  const { plantId, zoneId, serviceProvider } = props;

  const [showSiteViewDashboardFilters, setShowSiteViewDashboardFilters] =
    useState(false);
  const [vrmSolarPlantAlerts, setVrmSolarPlantAlerts] = useState<
    IVrmSolarPlantAlert[]
  >([]);
  const [goodWeSolarPlantAlerts, setGoodWeSolarPlantAlerts] = useState<
    IGoodWeSolarPlantAlert[]
  >([]);
  const [filters, setFilters] = useState<IRequestParams>({ ...initialFilters });
  const [pageSize, setPageSize] = useState(10);

  const [triggerSolarPlantAlertsAdmin, { isFetching }] =
    useLazyViewSolarPlantAlertsAdminQuery();

  useEffect(() => {
    if (plantId) {
      const filterParams: IViewAdminSolarPlantAlertsRequestDTO = {
        plantId: plantId,
        pageNumber: 0,
        pageSize: pageSize,
      };

      if (filters.startDate) {
        filterParams.start = filters.startDate;
      }

      if (filters.endDate) {
        filterParams.end = filters.endDate;
      }

      triggerSolarPlantAlertsAdmin({ ...filterParams })
        .unwrap()
        .then((res: IViewAdminSolarPlantAlertsResponseDTO) => {
          setVrmSolarPlantAlerts(res.alerts as IVrmSolarPlantAlert[]);
          setGoodWeSolarPlantAlerts(res.alerts as IGoodWeSolarPlantAlert[]);
        })
        .catch((error) => {
          setVrmSolarPlantAlerts([]);
        });
    }
  }, [triggerSolarPlantAlertsAdmin, plantId, filters, pageSize]);

  const updateFilters = (startDate: string, endDate: string) => {
    setFilters({
      startDate,
      endDate,
    });
  };

  return (
    <div className="position-relative">
      <Row className="row-cols-auto justify-content-between">
        <Col>
          <AppButton
            iconName="open_in_new"
            iconWeight={700}
            text={`Open in ${
              serviceProvider === ESolarServiceProvider.VRM
                ? `Victron VRM`
                : `GoodWe`
            }`}
            variant="transparentWithBorder"
            className="font-size-14"
            onClick={() =>
              serviceProvider === ESolarServiceProvider.VRM
                ? window.open("https://vrm.victronenergy.com/", "_blank")
                : window.open("https://www.semsportal.com/", "_blank")
            }
          />
        </Col>
        <Col>
          <ButtonWithBadge
            text="Filter"
            icon="filter_alt"
            onClick={() => setShowSiteViewDashboardFilters(true)}
          />
        </Col>
      </Row>
      <div className="bg-white">
        {serviceProvider === ESolarServiceProvider.VRM ? (
          <Row className="mt-4">
            <Col>
              <SitesErrorLogTable
                headers={[
                  { id: 1, columnName: "Start Time" },
                  { id: 2, columnName: "Instance" },
                  { id: 3, columnName: "Device" },
                  { id: 4, columnName: "Description" },
                  { id: 5, columnName: "Cleared After" },
                ]}
                data={vrmSolarPlantAlerts.map((vrmSolarPlantAlert) => {
                  return {
                    data: [
                      <>
                        {formatEpochToDate(vrmSolarPlantAlert.started, zoneId)}
                      </>,
                      <>{vrmSolarPlantAlert.instance}</>,
                      <>
                        {vrmSolarPlantAlert.device
                          ? vrmSolarPlantAlert.device
                          : "Unknown"}
                      </>,
                      <>
                        {vrmSolarPlantAlert.description}:&nbsp;
                        {vrmSolarPlantAlert.nameEnum}
                      </>,
                      <>
                        {vrmSolarPlantAlert.isActive === 1
                          ? `Active`
                          : vrmSolarPlantAlert.started &&
                            vrmSolarPlantAlert.cleared
                          ? getTimestampDuration(
                              vrmSolarPlantAlert.started,
                              vrmSolarPlantAlert.cleared
                            )
                          : "-"}
                      </>,
                    ],
                    cellWidths: [150, 100, 200, 550, 150],
                    isActive: vrmSolarPlantAlert.isActive === 1,
                  };
                })}
              />
            </Col>
          </Row>
        ) : (
          <Row className="mt-4 px-3">
            <Col>
              <DataTable
                headers={[
                  { id: 1, columnName: "SN" },
                  { id: 2, columnName: "Device Type" },
                  { id: 3, columnName: "Description" },
                  { id: 4, columnName: "Fault Level" },
                  { id: 5, columnName: "Fault Classification" },
                  { id: 6, columnName: "Status" },
                  { id: 7, columnName: "Time" },
                ]}
                data={goodWeSolarPlantAlerts.map((goodWeSolarPlantAlert) => {
                  return {
                    data: [
                      <>{goodWeSolarPlantAlert.devicesn}</>,
                      <>{translate(goodWeSolarPlantAlert.device_type)}</>,
                      <>{goodWeSolarPlantAlert.warningname}</>,
                      <>{translate(goodWeSolarPlantAlert.standard_level)}</>,
                      <>
                        {goodWeSolarPlantAlert.fault_classification
                          .map((i) => translate(i))
                          .join(", ")}
                      </>,
                      <>
                        <div
                          className={
                            goodWeSolarPlantAlert.status
                              ? `text-light`
                              : `text-red`
                          }
                        >
                          {goodWeSolarPlantAlert.status
                            ? `Recovered`
                            : `Happening`}
                        </div>
                      </>,
                      <>
                        {moment
                          .utc(
                            goodWeSolarPlantAlert.status
                              ? goodWeSolarPlantAlert.recoverytime
                              : goodWeSolarPlantAlert.happentime
                          )
                          .format("DD/MM/YY HH:mm:ss")}
                      </>,
                    ],
                  };
                })}
              />
            </Col>
          </Row>
        )}
        {(vrmSolarPlantAlerts.length > 9 ||
          goodWeSolarPlantAlerts.length > 9) && (
          <Row
            className="justify-content-center cursor-pointer pb-2"
            onClick={() => setPageSize(pageSize + 10)}
          >
            <Col className="col-auto">
              <Row className="justify-content-center">
                <Col className="col-auto text-center text-primary font-weight-500 font-size-11">
                  Show More
                </Col>
              </Row>
              <Row className="justify-content-center">
                <Col className="col-auto text-center">
                  <MaterialIcon
                    icon="stat_minus_1"
                    color="#1A3C84"
                    size={18}
                    weight={500}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        )}
      </div>
      <SiteErrorLogFilterPanel
        show={showSiteViewDashboardFilters}
        onClose={() => setShowSiteViewDashboardFilters(false)}
        updateFilters={updateFilters}
        zoneId={zoneId}
      />
      <SpinnerModal show={isFetching} />
    </div>
  );
};

export default SiteErrorLog;
